import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ServiceRequestService } from '@common/services/service-request.service';
import { regexValidator } from '@common/validators/regex-validator';
import { requiredValidator } from '@common/validators/required-validator';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { ConfirmMovesDisplayModel } from 'app/cargo-operations/models/ConfirmMoves.display.model';

@Component({
    selector: 'app-confirm-moves-modal',
    templateUrl: './confirm-moves-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfirmMovesModalComponent implements OnInit {
    title: string;
    message: string;
    manipulation: string;
    containerItemIds: any[];
    serviceRequestId: string;
    canceledSeals: any[] = [];
    isUnnamedContainer: boolean = false;

    model: ConfirmMovesDisplayModel = new ConfirmMovesDisplayModel();
    form: UntypedFormGroup;

    sealArrayRef: UntypedFormArray;

    constructor(
        public activeModal: DialogRef,
        public serviceRequestService: ServiceRequestService
    ) {}

    ngOnInit() {
        this.form = this.model.createFormGroup();
        this.form.get('containerCount').patchValue(this.containerItemIds.length);
        this.form.get('serviceRequestId').patchValue(this.serviceRequestId);
        this.form.get('manipulation').patchValue(this.manipulation);
        const containerNumberControl = this.form.get('containerNumber');
        if (this.containerItemIds.length === 1) {
            if (this.isUnnamedContainer) {
                containerNumberControl.setValidators([
                    requiredValidator(),
                    regexValidator('^(?=.{11}$)[A-Z]{4}\\d{7}$')
                ]);
            } else {
                containerNumberControl.setValidators([regexValidator('^(?=.{11}$)[A-Z]{4}\\d{7}$')]);
            }
        } else {
            containerNumberControl.clearValidators();
        }
        containerNumberControl.updateValueAndValidity();

        this.sealArrayRef = this.form.get('seals') as UntypedFormArray;

        if (this.containerItemIds.length == 1) {
            this.getSeals();
        }
    }

    getSeals() {
        this.serviceRequestService.getSealsForContainer(this.containerItemIds[0]).subscribe((data: any) => {
            if (data?.length > 0) {
                data.forEach((element) => {
                    const isPreloaded = !!element.id;
                    const newControl = new UntypedFormArray([
                        new UntypedFormControl({ value: element.sealNumber, disabled: isPreloaded }),
                        new UntypedFormControl({ value: element.sealType, disabled: isPreloaded }),
                        new UntypedFormControl({ value: element.sealRemark, disabled: isPreloaded }),
                        new UntypedFormControl({ value: element.canceled, disabled: true }),
                        new UntypedFormControl({ value: false, disabled: true }),
                        new UntypedFormControl({ value: element.id, disabled: true })
                    ]);

                    this.sealArrayRef.push(newControl);
                });
            }
        });
    }

    addSeal() {
        const newControl = new UntypedFormArray([
            new UntypedFormControl({ value: null, disabled: false }),
            new UntypedFormControl({ value: null, disabled: false }),
            new UntypedFormControl({ value: null, disabled: false }),
            new UntypedFormControl({ value: false, disabled: true }),
            new UntypedFormControl({ value: true, disabled: false }),
            new UntypedFormControl({ value: 0, disabled: false })
        ]);

        this.sealArrayRef.push(newControl);
    }

    cancelSeal(index: number): void {
        const sealGroup = this.sealArrayRef.at(index) as UntypedFormArray;

        const canceledSeal = {
            id: sealGroup.at(5).value,
            sealNumber: sealGroup.at(0).value,
            sealType: sealGroup.at(1).value,
            sealRemark: sealGroup.at(2).value,
            sealCanceled: true,
            additional: sealGroup.at(4).value
        };

        this.canceledSeals.push(canceledSeal);

        this.sealArrayRef.removeAt(index);
    }

    clear(data: any) {
        this.form.reset();
    }

    save() {
        const body = this.form.getRawValue();

        body['containerItemIds'] = this.containerItemIds;

        if (body.seals?.length > 0 || this.canceledSeals.length > 0) {
            body.moveSeals = [];

            if (body.seals?.length > 0) {
                body.seals.forEach((element) => {
                    body.moveSeals.push({
                        id: element[5],
                        sealNumber: element[0],
                        sealType: element[1],
                        sealRemark: element[2],
                        sealCanceled: element[3],
                        additional: element[4]
                    });
                });
            }

            body.moveSeals.push(...this.canceledSeals);
        }

        this.activeModal.close(body);
    }
}
